import React from 'react';
import { connect } from 'react-redux';
import { AppState } from 'acscp/store';
import { Booking as BookingElement } from 'acscp/store';
import style from './bookingsList.module.scss';
import { Booking } from './booking';
import { ArrowIcon } from 'acscp/components/common/icons/arrowIcon';
import { BookingsFilter } from 'acscp/store';
import { isBookingRead } from 'acscp/store/helpers/isBookingRead';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { formatBookingsFilter } from 'acscp/components/common/helpers/formatBookingFilter';
import { withCollapsable } from 'acscp/components/common/collapsable';
import { TitleProps } from 'acscp/components/common/collapsable';
import { Background, Row } from 'acscp/components/common/row';
import { NewFlightRequestButton } from '../flightRequest/newFlightRequestButton';
import { userCanPotentiallyRequestFlights } from 'acscp/store/helpers/authHelper';

export interface ReadValuesDictionary {
    [id: string]: boolean;
}

interface BookingsListStateProps {
    bookingsReadValues: ReadValuesDictionary;
    canPotentiallyRequestFlights: boolean;
}

interface BookingsListOwnProps {
    bookingsFilter: BookingsFilter;
    bookings: BookingElement[];
    id: string;
}

type BookingsListProps = BookingsListOwnProps & BookingsListStateProps;

function hasUnreadBookings(bookings: BookingElement[], bookingsReadValues: ReadValuesDictionary): any {
    return bookings.some(b => !bookingsReadValues[b.id]);
}

const BookingsListContent: React.SFC<BookingsListProps> = ({bookings, bookingsReadValues, canPotentiallyRequestFlights}) => {
    // Returning null will completely hide the section. Instead return a dummy div.
    if (bookings.length === 0) {
        return <div/>;
    }

    return (
        <Row className={style.List}>
            {bookings.map((booking, index) => (
                <div key={index} className={style.ListItem}>
                    <Booking
                        booking={booking}
                        read={bookingsReadValues[booking.id]}
                        canPotentiallyRequestFlights={canPotentiallyRequestFlights}
                    />
                </div>
            ))}
        </Row>
    );
};

class BookingsListTitle extends React.Component<TitleProps & BookingsListProps, {}> {

    componentDidMount(): void {
        this.updateOwnCollapsedState(this.props);
    }
    componentWillReceiveProps(nextProps: TitleProps & BookingsListProps): void {
        this.updateOwnCollapsedState(nextProps);
    }
    private updateOwnCollapsedState(props: TitleProps & BookingsListProps): void {
        if (props.setCollapsed) {
            // Collapsable section without any elements can not be collapsed or expanded.
            // Mark this section as having undefined collapsed state.
            if (props.bookings.length === 0 && props.collapsed !== undefined) {
                props.setCollapsed(undefined);
            }
        }
    }

    render(): JSX.Element {
        const { id, bookings, bookingsFilter, bookingsReadValues, collapsed, setCollapsed = () => {} } = this.props;

        const headerTextStyle = classNames(
            style.HeaderText,
            {
                [style.Hidden]: collapsed !== false,
                [style.Inactive]: bookings.length === 0
            }
        );
        const addEnquiriesButton = bookingsFilter === BookingsFilter.Enquiries && this.props.canPotentiallyRequestFlights;

        return (
            <Row
                background={Background.Grey}
                className={style.Header}
                onClick={() => setCollapsed(!collapsed)}
            >
                <div className={headerTextStyle} id={id}>
                    <ArrowIcon />
                    {formatBookingsFilter(bookingsFilter)}
                </div>
                <span
                    className={classNames(style.ItemCount, {
                        [style.Unread]: hasUnreadBookings(bookings, bookingsReadValues)
                    })}
                >
                    <span className={style.Count}>
                        {bookings.length}
                    </span> <span className={style.CountLabel}>
                        <FormattedMessage
                            id={'components.bookings.bookingsList.items'}
                            defaultMessage={'{itemCount, plural, one {Item} other {Items}}'}
                            values={{itemCount: bookings.length}}
                        />
                    </span>
                </span>
                {addEnquiriesButton &&
                    /* Stop propagation, otherwise the section is "collapsed" whenever the user clicks anywhere on the modal */
                    <div className={style.NewEnquiryWrapper} onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}>
                        <NewFlightRequestButton centreAlign={false} />
                    </div>}
            </Row>
        );
    }
}

export class BookingsListPresentational extends React.Component<TitleProps & BookingsListProps, {}> {
    // React is only performing a shallow comparison of the props.
    // This is causing React to think that arrays in the props are new and so re-renders everything,
    // whereas they often have the same contents. We perform a deep comparison to check.
    shouldComponentUpdate(nextProps: TitleProps & BookingsListOwnProps): boolean {
        return JSON.stringify(nextProps) !== JSON.stringify(this.props);
    }

    render(): JSX.Element {
        const props = this.props;
        const CollapsableBookingsList = withCollapsable(
            (
                <BookingsListTitle
                    id={props.id}
                    bookingsFilter={props.bookingsFilter}
                    bookingsReadValues={props.bookingsReadValues}
                    bookings={props.bookings}
                    canPotentiallyRequestFlights={props.canPotentiallyRequestFlights}
                />
            )
            , BookingsListContent);

        return (
            <div
                className={style.ChartersSection}
            >
                <CollapsableBookingsList {...props} />
            </div >
        );
    }
}

function mapStateToProps(state: AppState, ownProps: BookingsListOwnProps): BookingsListStateProps {
    const bookingsReadValues: ReadValuesDictionary = {};

    ownProps.bookings.forEach(b => {
        bookingsReadValues[b.id] = isBookingRead(b, state.bookingsReadDetails);
    });

    return {
        bookingsReadValues,
        canPotentiallyRequestFlights: userCanPotentiallyRequestFlights(state.auth),
    };
}

export const BookingsList = connect(mapStateToProps)(BookingsListPresentational);
